import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import hwAxios from '../../../../rmsReduxStore/api-service/HWAxios';
import { POLL_INTERVAL_MS, isExecutionFinished } from './executionLogs.config';
import { type IRemoteCommandExecutionLogs } from './RemoteCommandExecutionLogs';

const url = process.env.REACT_APP_SERVER_BASE_URL;

const EMPTY_LOGS: IRemoteCommandExecutionLogs = { currentExecution: null, mostRecentExecution: null };

// Pass null as remoteCommand for the commands without execution logs
const useRemoteCommandExecutionLogs = (chargerId: string | null, remoteCommand: string | null) => {
  const [executionLogs, setExecutionLogs] = useState<IRemoteCommandExecutionLogs>(EMPTY_LOGS);
  const [isLogsLoaded, setIsLogsLoaded] = useState(false);

  // Used to ignore the response of an outdated request, e.g. the command was switched before the response came back
  const latestRequestIdRef = useRef(0);

  const fetchExecutionLogs = useCallback(async () => {
    if (!chargerId || !remoteCommand) return;

    const requestId = ++latestRequestIdRef.current;
    try {
      const response = await hwAxios.post(url + '/charger/remote-command/execution-logs', { chargerId, remoteCommand });
      if (requestId !== latestRequestIdRef.current) return;

      setExecutionLogs({
        currentExecution: response?.data?.data?.currentExecution ?? null,
        mostRecentExecution: response?.data?.data?.mostRecentExecution ?? null,
      });
    } catch (error) {
      console.error(error);
    } finally {
      if (requestId === latestRequestIdRef.current) setIsLogsLoaded(true);
    }
  }, [chargerId, remoteCommand]);

  useEffect(() => {
    setExecutionLogs(EMPTY_LOGS);
    setIsLogsLoaded(false);
    fetchExecutionLogs();
  }, [fetchExecutionLogs]);

  // Ignore the request still in flight once the screen is closed
  useEffect(() => {
    return () => { latestRequestIdRef.current++; };
  }, []);

  const isExecutionInProgress = !!executionLogs?.currentExecution && !isExecutionFinished(executionLogs?.currentExecution);

  useEffect(() => {
    if (!isExecutionInProgress) return;
    const pollInterval = setInterval(fetchExecutionLogs, POLL_INTERVAL_MS);
    return () => clearInterval(pollInterval);
  }, [isExecutionInProgress, fetchExecutionLogs]);

  return useMemo(() => ({
    ...executionLogs,
    isLogsLoaded,
    isExecutionInProgress,
    fetchExecutionLogs,
  }), [executionLogs, isLogsLoaded, isExecutionInProgress, fetchExecutionLogs]);
};

export default useRemoteCommandExecutionLogs;
